/**
 * The permission catalogue, mirroring Daghfal.Domain.Authorization.Permissions.
 *
 * The clients check these keys and never a role name — exactly as the API does — so that an owner who
 * withholds one capability from one staff member gets the interface they expect without a new role being
 * invented for them (FR-RBAC-04).
 *
 * Hiding a control here is a courtesy to the user, not a security boundary. Every one of these keys is
 * enforced again on the server; the client only avoids showing a button that would be refused.
 */
export const Permissions = {
  children: {
    viewAssigned: 'children.view.assigned',
    viewAll: 'children.view.all',
    create: 'children.create',
    update: 'children.update',
    transfer: 'children.transfer',
    withdraw: 'children.withdraw',
    export: 'children.export',
    /** The owner's alone (Q37). */
    legalHold: 'children.legal_hold',
  },
  daily: {
    record: 'daily.record',
    publish: 'daily.publish',
    viewAssigned: 'daily.view.assigned',
    viewAll: 'daily.view.all',
  },
  photos: {
    upload: 'photos.upload',
    publish: 'photos.publish',
    delete: 'photos.delete',
  },
  attendance: {
    record: 'attendance.record',
    correct: 'attendance.correct',
    viewAll: 'attendance.view.all',
  },
  messages: {
    direct: 'messages.direct',
    classBroadcast: 'messages.class_broadcast',
    tenantBroadcast: 'messages.tenant_broadcast',
  },
  finance: {
    view: 'finance.view',
    recordPayment: 'finance.payment.record',
    managePlan: 'finance.plan.manage',
    issueInvoice: 'finance.invoice.issue',
    generateStatement: 'finance.statement.generate',
    sendReminder: 'finance.reminder.send',
  },
  staff: {
    view: 'staff.view',
    invite: 'staff.invite',
    update: 'staff.update',
    managePermissions: 'staff.permissions.manage',
    deactivate: 'staff.deactivate',
  },
  /**
   * Complaints (§4.14).
   *
   * Hiding the queue from a teacher here is the usual courtesy. What is *not* a courtesy is the subject
   * rule: a complaint that names you is withheld by the API whatever you hold, so a console that showed the
   * nav entry to someone who is the subject of every open complaint would still show them an empty list.
   */
  complaints: {
    view: 'complaints.view',
    respond: 'complaints.respond',
    assign: 'complaints.assign',
    close: 'complaints.close',
  },
  health: {
    view: 'health.view',
    manage: 'health.manage',
    createIncident: 'incident.create',
    closeIncident: 'incident.close',
    administerMedication: 'medication.administer',
  },
  accounts: {
    /** Confirm people in person, move an account to a new number, or release one (Q25, Q27). Owner and manager. */
    recover: 'accounts.recover',
  },
  academic: {
    manage: 'academic.manage',
    /** The owner's alone (Q34). */
    reopen: 'academic.reopen',
    /** Every year's history, read only (Q35). */
    viewArchive: 'archive.view',
  },
  tenant: {
    manageClasses: 'classes.manage',
    manageSettings: 'settings.manage',
    viewReports: 'reports.view',
    viewAudit: 'audit.view',
    approveSupport: 'support.approve',
  },
  platform: {
    viewTenants: 'platform.tenants.view',
    createTenants: 'platform.tenants.create',
    updateTenants: 'platform.tenants.update',
    suspendTenants: 'platform.tenants.suspend',
    archiveTenants: 'platform.tenants.archive',
    managePlans: 'platform.plans.manage',
    manageBilling: 'platform.billing.manage',
    manageUsers: 'platform.users.manage',
    sendAnnouncements: 'platform.announcements.send',
    viewAudit: 'platform.audit.view',
    /** Decide waiting resets, renumber, release, and hand out recovery links (Q25, Q27). */
    recoverIdentities: 'platform.identities.recover',
    requestSupport: 'platform.support.request',
    /** List, make and test backups (Q65). */
    manageBackups: 'platform.backups.manage',
    /** Restore the whole platform from a backup (Q65). */
    restoreBackups: 'platform.backups.restore',
    /** Keep the allergen catalogue, and read the names it does not know yet (Q75, Q77). */
    manageAllergens: 'platform.allergens.manage',
    /** Switch a tenant's modules on and off (NR-11). */
    manageModules: 'platform.modules.manage',
  },
} as const;

/** Role keys, from Daghfal.Domain.Authorization.SystemRoles. Used for routing and copy, never for access. */
export const Roles = {
  platform: {
    superadmin: 'superadmin',
    supportEmployee: 'support_employee',
    billingEmployee: 'billing_employee',
  },
  tenant: {
    owner: 'owner',
    manager: 'manager',
    accountant: 'accountant',
    teacher: 'teacher',
    assistant: 'assistant',
    reception: 'reception',
    nurse: 'nurse',
    guardian: 'guardian',
  },
} as const;

export type PermissionKey = string;
